import React, { useState, useRef, ReactNode, useEffect } from 'react';
import throttle from 'lodash/throttle';
import { SecondaryButton, ButtonIcon } from '@hz-design-system/web-ui';

import Classes from './HorizontalScrollList.module.scss';

type TProps = {
  children: ReactNode;
  showScrollbar?: boolean;
};

const DEFAULT_THROTTLE = 300;

const HorizontalScrollList = ({ children, showScrollbar = false }: TProps) => {
  const [isTablet, setIsTablet] = useState(false);
  const [hasScrollButtons, setHasScrollButtons] = useState(false);
  const [showScrollButtonLeft, setShowScrollButtonLeft] = useState(false);
  const [showScrollButtonRight, setShowScrollButtonRight] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tabletSize = getComputedStyle(document.documentElement).getPropertyValue('--is-tablet') === '1';
    setIsTablet(tabletSize);
  }, []);

  useEffect(() => {
    if (isTablet) {
      const listWidth = listRef?.current?.offsetWidth || 0;
      const listScrollWidth = listRef?.current?.scrollWidth || 0;
      setHasScrollButtons(listScrollWidth > listWidth);
    } else {
      setHasScrollButtons(false);
    }
  }, [children, isTablet]);

  const handleScroll = () => {
    if (listRef?.current) {
      const scrollOffset = 15;
      const scrollLeftValue = listRef.current.scrollLeft;
      const listWidth = listRef.current.offsetWidth;
      const listScrollWidth = listRef.current.scrollWidth;

      setShowScrollButtonLeft(scrollLeftValue > scrollOffset);
      setShowScrollButtonRight(scrollLeftValue + listWidth + scrollOffset < listScrollWidth);
    }
  };

  const scrollList = (direction: 'left' | 'right') => {
    if (listRef?.current) {
      const scrollOffset = 200;
      const scrollByValue = listRef.current.offsetWidth - scrollOffset;
      const directionScrollByValue = direction === 'left' ? scrollByValue * -1 : scrollByValue;

      listRef.current.scrollBy({
        left: directionScrollByValue,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className={Classes.root}>
      {hasScrollButtons && showScrollButtonLeft && (
        <div className={Classes.scrollButtonWrapperLeft}>
          <SecondaryButton className={Classes.scrollButton} onClick={() => scrollList('left')}>
            <ButtonIcon name="arrow-left-regular" />
          </SecondaryButton>
        </div>
      )}
      <div
        className={`${Classes.list}${!showScrollbar ? ' u-hideScrollbar' : ''}`}
        onScroll={hasScrollButtons ? throttle(handleScroll, DEFAULT_THROTTLE) : undefined}
        ref={listRef}
      >
        {children}
      </div>
      {hasScrollButtons && showScrollButtonRight && (
        <div className={Classes.scrollButtonWrapperRight}>
          <SecondaryButton className={Classes.scrollButton} onClick={() => scrollList('right')}>
            <ButtonIcon name="arrow-right-regular" />
          </SecondaryButton>
        </div>
      )}
    </div>
  );
};

export default HorizontalScrollList;
